import { useCallback, useEffect, useRef, useState } from 'react'

import {
  cartFromValidationError,
  type PosCartData,
  type InventoryAllocation,
} from '@/app/services/pos.service'

interface CartQuantityClient {
  updateCartItem: (
    productId: number,
    payload: {
      quantity: number
      expected_version: number
      allocations?: InventoryAllocation[]
    },
  ) => Promise<PosCartData>
  deleteCartItem: (productId: number) => Promise<PosCartData>
}

interface QueuedCartQuantityOptions {
  cart: PosCartData | undefined
  client: CartQuantityClient
  onCartChange: (cart: PosCartData) => void
  onError: (error: unknown) => void
}

export function useQueuedCartQuantityUpdates({
  cart,
  client,
  onCartChange,
  onError,
}: QueuedCartQuantityOptions) {
  const cartRef = useRef(cart)
  const clientRef = useRef(client)
  const onCartChangeRef = useRef(onCartChange)
  const onErrorRef = useRef(onError)
  const pendingDeltasRef = useRef(new Map<number, number>())
  const runningRef = useRef(new Set<number>())
  const mountedRef = useRef(true)
  const [pendingProductIds, setPendingProductIds] = useState<Set<number>>(() => new Set())

  useEffect(() => {
    cartRef.current = cart
  }, [cart])

  useEffect(() => {
    clientRef.current = client
    onCartChangeRef.current = onCartChange
    onErrorRef.current = onError
  }, [client, onCartChange, onError])

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
    }
  }, [])

  const markPending = useCallback((productId: number, pending: boolean) => {
    if (!mountedRef.current) return

    setPendingProductIds((current) => {
      const next = new Set(current)
      if (pending) next.add(productId)
      else next.delete(productId)

      return next
    })
  }, [])

  const applyCart = useCallback((nextCart: PosCartData) => {
    cartRef.current = nextCart
    onCartChangeRef.current(nextCart)
  }, [])

  const drain = useCallback(async (productId: number) => {
    if (runningRef.current.has(productId)) return

    runningRef.current.add(productId)
    markPending(productId, true)
    let conflictRetries = 0

    try {
      while ((pendingDeltasRef.current.get(productId) ?? 0) !== 0) {
        const delta = pendingDeltasRef.current.get(productId) ?? 0
        pendingDeltasRef.current.set(productId, 0)
        const currentItem = cartRef.current?.items.find((item) => item.id === productId)

        if (!currentItem) break

        try {
          const targetQuantity = currentItem.quantity + delta
          const nextCart = targetQuantity <= 0
            ? await clientRef.current.deleteCartItem(productId)
            : await clientRef.current.updateCartItem(productId, {
                quantity: targetQuantity,
                expected_version: currentItem.version,
              })

          applyCart(nextCart)
          conflictRetries = 0
        } catch (error) {
          const latestCart = cartFromValidationError(error)
          if (latestCart) applyCart(latestCart)

          const responseStatus = (error as { response?: { status?: number } })?.response?.status
          if (responseStatus === 409 && latestCart && conflictRetries < 1) {
            pendingDeltasRef.current.set(
              productId,
              delta + (pendingDeltasRef.current.get(productId) ?? 0),
            )
            conflictRetries += 1
            continue
          }

          pendingDeltasRef.current.set(productId, 0)
          onErrorRef.current(error)
          break
        }
      }
    } finally {
      pendingDeltasRef.current.delete(productId)
      runningRef.current.delete(productId)
      markPending(productId, false)
    }
  }, [applyCart, markPending])

  const enqueue = useCallback((productId: number, delta: number) => {
    if (!delta || !cartRef.current?.items.some((item) => item.id === productId)) return

    pendingDeltasRef.current.set(
      productId,
      (pendingDeltasRef.current.get(productId) ?? 0) + delta,
    )
    void drain(productId)
  }, [drain])

  return {
    enqueue,
    isPending: pendingProductIds.size > 0,
    isProductPending: (productId: number) => pendingProductIds.has(productId),
  }
}
